import React, { type ReactNode } from "react";
import { Flex, type FlexProps, type SpaceProps } from "@chakra-ui/react";
import { TextTitle } from "ui/text";
import { type BrandColor } from "contexts/color-theme";
import { BookDemoButton } from "components/event-tracking";
import { Breadcrumbs, type BreadcrumbsProps } from "ui/breadcrumbs";
import { LightGradientBackground } from "./light-gradient-background";

export type GradientHeroProps = {
  /**
   * Color variant to use for the background effects.
   * */
  variant: BrandColor;

  /**
   * Optional "eyebrow" text that will be displayed as a chip above the title.
   */
  eyebrow?: string;

  /**
   * Main `h1` title.
   */
  title: ReactNode;

  /**
   * Description displayed with the `h1` title.
   */
  description: string;

  /**
   * The media to display.
   */
  media: ReactNode;

  /**
   * The maximum width of the media.
   *
   * @default {base: "100%", lg: "450px", xl: "500px" }
   */
  mediaMaxW?: FlexProps["maxW"];

  /**
   * Gap between the title and media.
   *
   * @default { base: "bigger", sm: "biggest" }
   */
  gap?: FlexProps["gap"];

  demoButtonText?: string;

  /**
   * Optional navigation breadcrumbs to show above the title.
   */
  breadcrumbs?: BreadcrumbsProps["crumbs"];
} & SpaceProps;

/**
 * A basic half-text, half-media hero component with a light gradient background.
 */
export function GradientHalfHero({
  variant,
  eyebrow,
  title,
  description,
  media,
  demoButtonText,
  mediaMaxW = { base: "100%", lg: "450px", xl: "500px" },
  gap = { base: "bigger", sm: "biggest" },
  breadcrumbs,
  ...spaceProps
}: GradientHeroProps) {
  return (
    <LightGradientBackground variant={variant} {...spaceProps}>
      {breadcrumbs && <Breadcrumbs hasPrimaryText crumbs={breadcrumbs} />}
      <Flex pos="relative" direction={{ base: "column", lg: "row" }} alignItems="center" gap={gap}>
        <TextTitle
          size="xl"
          titleAs="h1"
          descriptionAs="h2"
          chip={eyebrow ? { text: eyebrow } : undefined}
          title={title}
          description={description}
          actions={<BookDemoButton text={demoButtonText} />}
          pt={{ base: "bigger", sm: "biggest", xl: "huge" }}
          pb={{ base: 0, lg: "biggest" }}
        />

        <Flex flexShrink={0} justify="center" w="100%" maxW={mediaMaxW}>
          {media}
        </Flex>
      </Flex>
    </LightGradientBackground>
  );
}
